import { useState, useCallback } from 'react';
import { AcademyCreatorCard } from './AcademyCreatorCard';
import { AcademyVideoCard } from './AcademyVideoCard';
import { AcademyRejectDialog } from './AcademyRejectDialog';
import type { AcademyCreatorItem, AcademyVideoItem } from '../types';
import { useAcademyVideos, useBulkRejectVideos } from '../hooks/useAcademyAdmin';
import { Video, CheckSquare, Square, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AcademyCreatorWithVideosProps {
    creator: AcademyCreatorItem;
    isExpanded: boolean;
    videoStatusFilter: 'all' | 'approved' | 'pending' | 'rejected';
    onToggle: () => void;
    onApproveCreator: () => void;
    onRejectCreator: () => void;
    onApproveVideo: (video: AcademyVideoItem) => void;
    onRejectVideo: (video: AcademyVideoItem) => void;
    onDeleteVideo: (video: AcademyVideoItem) => void;
    onToggleVideoHidden?: (video: AcademyVideoItem) => void;
    onEditCategories?: (video: AcademyVideoItem) => void;
    onGoToImport?: (creator: AcademyCreatorItem) => void;
    isActioning: boolean;
}

export function AcademyCreatorWithVideos({
    creator,
    isExpanded,
    videoStatusFilter,
    onToggle,
    onApproveCreator,
    onRejectCreator,
    onApproveVideo,
    onRejectVideo,
    onDeleteVideo,
    onToggleVideoHidden,
    onEditCategories,
    onGoToImport,
    isActioning,
}: AcademyCreatorWithVideosProps) {
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedVideoIds, setSelectedVideoIds] = useState<Set<string>>(new Set());
    const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);

    const { data: videos = [], isLoading: videosLoading } = useAcademyVideos(
        videoStatusFilter,
        isExpanded ? creator.id : null,
        ''
    );
    const bulkRejectMutation = useBulkRejectVideos();

    const toggleSelectVideo = useCallback((videoId: string, checked: boolean) => {
        setSelectedVideoIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(videoId);
            else next.delete(videoId);
            return next;
        });
    }, []);

    const selectAllVideos = useCallback(() => {
        const rejectable = videos.filter((v) => v.status !== 'rejected');
        if (selectedVideoIds.size === rejectable.length) {
            setSelectedVideoIds(new Set());
        } else {
            setSelectedVideoIds(new Set(rejectable.map((v) => v.id)));
        }
    }, [videos, selectedVideoIds.size]);

    const clearSelection = useCallback(() => {
        setSelectedVideoIds(new Set());
        setSelectionMode(false);
    }, []);

    const handleBulkRejectConfirm = useCallback(
        (reason: string) => {
            const ids = Array.from(selectedVideoIds);
            if (ids.length === 0) return;
            bulkRejectMutation.mutate(
                { videoIds: ids, rejectionReason: reason || undefined },
                {
                    onSuccess: () => {
                        setSelectedVideoIds(new Set());
                        setShowBulkRejectDialog(false);
                        setSelectionMode(false);
                    },
                }
            );
        },
        [selectedVideoIds, bulkRejectMutation]
    );

    const rejectableCount = videos.filter((v) => v.status !== 'rejected').length;
    const selectedCount = selectedVideoIds.size;

    return (
        <div className="space-y-2">
            <AcademyCreatorCard
                creator={creator}
                isExpanded={isExpanded}
                onToggle={onToggle}
                onApprove={onApproveCreator}
                onReject={onRejectCreator}
                onGoToImport={onGoToImport ? () => onGoToImport(creator) : undefined}
                isActioning={isActioning}
            />
            {isExpanded && (
                <div className="ml-4 pl-4 border-l-2 border-border/50 space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                            <Video size={12} />
                            Vídeos de este creador ({videos.length})
                        </h4>
                        {videos.length > 0 && (
                            <div className="flex items-center gap-2">
                                {!selectionMode ? (
                                    <button
                                        type="button"
                                        onClick={() => setSelectionMode(true)}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                                    >
                                        <CheckSquare size={14} />
                                        Seleccionar vídeos
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={selectAllVideos}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                                        >
                                            {selectedCount === rejectableCount ? (
                                                <Square size={14} />
                                            ) : (
                                                <CheckSquare size={14} />
                                            )}
                                            {selectedCount === rejectableCount ? 'Ninguno' : 'Seleccionar todos'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowBulkRejectDialog(true)}
                                            disabled={selectedCount === 0 || bulkRejectMutation.isPending}
                                            className={cn(
                                                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium',
                                                'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 disabled:opacity-50 transition-colors'
                                            )}
                                        >
                                            <XCircle size={14} />
                                            Rechazar seleccionados ({selectedCount})
                                        </button>
                                        <button
                                            type="button"
                                            onClick={clearSelection}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    {videosLoading ? (
                        <div className="space-y-2">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="h-16 rounded-lg border border-border bg-card animate-pulse" />
                            ))}
                        </div>
                    ) : videos.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">No hay vídeos</p>
                    ) : (
                        <div className="space-y-2">
                            {videos.map((v) => (
                                <AcademyVideoCard
                                    key={v.id}
                                    video={v}
                                    isExpanded={false}
                                    onToggle={() => {}}
                                    onApprove={() => onApproveVideo(v)}
                                    onReject={() => onRejectVideo(v)}
                                    onDelete={() => onDeleteVideo(v)}
                                    onToggleHidden={onToggleVideoHidden ? () => onToggleVideoHidden(v) : undefined}
                                    onEditCategories={onEditCategories ? () => onEditCategories(v) : undefined}
                                    isActioning={isActioning || bulkRejectMutation.isPending}
                                    compact
                                    selectable={selectionMode}
                                    selected={selectedVideoIds.has(v.id)}
                                    onSelectChange={(checked) => toggleSelectVideo(v.id, checked)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            <AcademyRejectDialog
                isOpen={showBulkRejectDialog}
                title={`Rechazar ${selectedCount} vídeo${selectedCount !== 1 ? 's' : ''} seleccionado${selectedCount !== 1 ? 's' : ''}`}
                type="video"
                onConfirm={handleBulkRejectConfirm}
                onCancel={() => setShowBulkRejectDialog(false)}
            />
        </div>
    );
}
